/**
 * Copyright 2021 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import StoreLocator from '../../StoreLocator';
import { useSetStore } from '@/features/fulfillment/hooks/useSetStore';
import { useStoreDetailsData } from '@/features/header/api/getStoreDetails';
import usePrevious from '@/utils/usePrevious';
import { useEffect, useRef } from 'react';

export type Props = {
  closeAllModals?: () => void;
  closeStoreModal: () => void;
  ariaLabelledBy?: string;
  searchValue?: string;
  redirectToStore?: boolean;
};

export const SearchStoreModal = ({
  closeStoreModal,
  closeAllModals,
  searchValue,
  redirectToStore,
}: Props) => {
  const { setStore } = useSetStore(closeStoreModal);
  const { data: storeDetails } = useStoreDetailsData();
  const prevStoreNumber = storeDetails?.storeNumber;
  const prevAddress1 = storeDetails?.address1;
  const prevStoreObject = { prevStoreNumber, prevAddress1 };
  const prevStore = usePrevious(prevStoreObject);
  const modalRef = useRef<HTMLDivElement>(null);
  const triggerButtonRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    // Store the triggering button reference
    triggerButtonRef.current = document.activeElement as HTMLElement;

    // Focus the modal or first interactive element
    if (modalRef.current) {
      const firstFocusable = modalRef.current.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      firstFocusable?.focus();
    }

    // Trap focus within modal
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        const focusableElements = modalRef.current?.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );

        if (focusableElements && focusableElements.length > 0) {
          const firstElement = focusableElements[0];
          const lastElement = focusableElements[focusableElements.length - 1];

          if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }

      if (e.key === 'Escape') {
        closeStoreModal();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // Return focus to trigger button when modal closes
      triggerButtonRef.current?.focus();
    };
  }, [closeStoreModal]);

  const handleModalCloseAllModals = () => {
    closeStoreModal();
    if (closeAllModals) {
      closeAllModals();
    }
  };

  return (
    <div ref={modalRef} role="dialog" aria-modal="true" aria-label="Find a Store">
      <StoreLocator
        searchValue={searchValue}
        storeDetails={storeDetails}
        setStore={setStore}
        handleClose={closeStoreModal}
        closeAllModals={handleModalCloseAllModals}
        prevStore={prevStore ?? undefined}
        redirectToStore={redirectToStore}
      />
    </div>
  );
};
